import { INITIATIVE_USER_ROLES, InitiativeUserRole } from './initiativeRoles'

type StringProperty = 'string' | { type: 'string' | 'text' | 'alias' }
type InferPropertyType<T> = T extends StringProperty
  ? string
  : T extends 'boolean'
    ? boolean
    : T extends 'number'
      ? number
      : T extends { type: 'timestamp' }
        ? number // Timestamps are best represented as numbers
        : T extends {
              props: { firstName: StringProperty; lastName: StringProperty }
            }
          ? { firstName: string; lastName: string }
          : T extends { props: infer P }
            ? SchemaToType<P> // Recursively infer for nested objects
            : T extends ReadonlyArray<infer U> // This handles the enum arrays from Object.values()
              ? U
              : T extends { ref: string }
                ? any
                : T extends { items: { ref: string } }
                  ? any[] // For simplicity, we'll type relations as any array. This could be made more specific.
                  : any // Fallback for any other types

// This main type maps over the schema object and applies the helper to each property
export type SchemaToType<S> = {
  // We use "-?" to make all properties required, as they are in the schema
  -readonly [K in keyof S]-?: InferPropertyType<S[K]>
}

export type DeepFilter<T> = {
  // Map over all keys of the generic type T and make them optional
  [K in keyof T]?: T[K] extends boolean
    ? boolean // If the property is a boolean, keep it as a boolean
    : T[K] extends string | number
      ? T[K][] // If it's a string or number, make it an array of that type
      : T[K] extends object
        ? DeepFilter<T[K]> // << RECURSIVE STEP: If it's an object, apply DeepFilter to it
        : T[K] // Otherwise, keep the original type
}

export const isUserAdmin = (
  user: { $role: InitiativeUserRole } | { role: InitiativeUserRole },
) => {
  const role = '$role' in user ? user.$role : user.role

  return (
    role === INITIATIVE_USER_ROLES.admin || role === INITIATIVE_USER_ROLES.owner
  )
}

export const isUserMemberOrFollower = (user: { $role: InitiativeUserRole }) => {
  return (
    user.$role === INITIATIVE_USER_ROLES.member ||
    user.$role === INITIATIVE_USER_ROLES.follower
  )
}
